import { computed, readonly, ref } from 'vue'
import { Capacitor } from '@capacitor/core'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
}

type InstallResult = 'accepted' | 'dismissed' | 'unavailable'
type InstallState = 'native' | 'installed' | 'prompt' | 'ios-help' | 'browser-help'

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
const isIos = ref(false)
const isNativeApp = ref(false)
const initialized = ref(false)

function getNavigatorWithStandalone(): Navigator & { standalone?: boolean } {
  return window.navigator as Navigator & { standalone?: boolean }
}

function checkIsIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
}

function checkIsStandalone(): boolean {
  const mediaStandalone = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = getNavigatorWithStandalone().standalone === true

  return mediaStandalone || iosStandalone
}

function updateState() {
  if (typeof window === 'undefined') return

  isNativeApp.value = Capacitor.isNativePlatform()
  isIos.value = checkIsIos()
  installed.value = checkIsStandalone()
}

function onBeforeInstallPrompt(event: Event) {
  event.preventDefault()
  deferredPrompt.value = event as BeforeInstallPromptEvent
  updateState()
}

function onAppInstalled() {
  installed.value = true
  deferredPrompt.value = null
}

function onVisibilityChange() {
  updateState()
}

function initPwaInstallPrompt() {
  if (initialized.value || typeof window === 'undefined') return

  initialized.value = true
  updateState()

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  window.addEventListener('appinstalled', onAppInstalled)
  window.addEventListener('focus', onVisibilityChange)
  document.addEventListener('visibilitychange', onVisibilityChange)
}

export function usePwaInstallPrompt() {
  initPwaInstallPrompt()

  const canInstall = computed(() => {
    return !isNativeApp.value && !installed.value && !!deferredPrompt.value
  })

  const showIosInstallHelp = computed(() => {
    return !isNativeApp.value && isIos.value && !installed.value
  })

  const showBrowserInstallHelp = computed(() => {
    return !isNativeApp.value && !isIos.value && !installed.value && !deferredPrompt.value
  })

  const installState = computed<InstallState>(() => {
    if (isNativeApp.value) return 'native'
    if (installed.value) return 'installed'
    if (canInstall.value) return 'prompt'
    if (showIosInstallHelp.value) return 'ios-help'

    return 'browser-help'
  })

  async function promptInstall(): Promise<InstallResult> {
    if (!deferredPrompt.value) {
      return 'unavailable'
    }

    const promptEvent = deferredPrompt.value
    await promptEvent.prompt()

    const choice = await promptEvent.userChoice

    deferredPrompt.value = null
    updateState()

    return choice.outcome === 'accepted' ? 'accepted' : 'dismissed'
  }

  return {
    canInstall: readonly(canInstall),
    installed: readonly(installed),
    isIos: readonly(isIos),
    isNativeApp: readonly(isNativeApp),
    showIosInstallHelp: readonly(showIosInstallHelp),
    showBrowserInstallHelp: readonly(showBrowserInstallHelp),
    installState: readonly(installState),
    promptInstall,
  }
}

export type { InstallResult, InstallState }
